import React, { Component } from 'react';
import { Terminal, ITerminalOptions } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash.debounce';
import classnames from 'classnames';

import { RemoteCapsule, Lifecycle, RemoteExec } from '@bit/amit.capsule-dev.remote-capsule';
import { CapsuleTermAddon } from '@bit/amit.capsule-dev.xterm.capsule-addon';

import 'xterm/css/xterm.css'; //!important
import styles from './terminal.module.scss';

type RxTermProps = {
	capsule?: RemoteCapsule;
};
type RxTermState = {
	connectionState?: Lifecycle;
};

const terminalOptions: ITerminalOptions = {
	disableStdin: true,
	cursorBlink: false,
	cursorStyle: 'bar',
	fontSize: 13,
	theme: {
		background: '#1E1E1E',
		foreground: '#EDEDED',
	},
};

export class RxTerm extends Component<RxTermProps, RxTermState> {
	xterm?: Terminal;
	anchorRef = React.createRef<HTMLDivElement>();
	state: RxTermState = { connectionState: undefined };

	private _resizeObserver = new ResizeObserver((entries, observer) => this.handleResize());
	private _fitter?: FitAddon;

	componentDidMount() {
		this._handleNewCapsule(this.props.capsule);
	}

	componentWillUnmount() {
		if (this.xterm) {
			this.xterm.dispose();
			this.xterm = undefined;
		}

		if (this.anchorRef.current) {
			this._resizeObserver.unobserve(this.anchorRef.current);
		}

		this._fitter = undefined;
	}

	componentDidUpdate(prevProps: RxTermProps) {
		const nextProps = this.props;

		if (prevProps.capsule !== nextProps.capsule) {
			this._handleNewCapsule(nextProps.capsule, prevProps.capsule);
		}
	}

	handleResize = debounce(() => {
		const { _fitter } = this;
		if (!_fitter) return;

		_fitter.fit();
	}, 300);

	private _handleNewCapsule(capsule?: RemoteCapsule, prevCapsule?: RemoteCapsule) {
		this.xterm && this.xterm.dispose();

		if (!capsule) return;
		this.setupXterm();

		const { connectionState } = capsule;
		this.setState({ connectionState });

		Object.values(Lifecycle)
			.map(x => x as Lifecycle)
			.forEach(x => capsule.on(x, () => this.handleConnectionChanged(x)));

		if (connectionState === Lifecycle.Connected) {
			this.start();
		}
	}

	setupXterm = () => {
		if (!this.anchorRef.current) return;

		const xterm = new Terminal(terminalOptions);
		this.xterm = xterm;
		xterm.open(this.anchorRef.current);

		this._fitter = new FitAddon();
		xterm.loadAddon(this._fitter);
		this._fitter.fit();

		this._resizeObserver.observe(this.anchorRef.current);
	};

	handleConnectionChanged = (nextState: Lifecycle) => {
		const prevState = this.state.connectionState;
		if (prevState === nextState) return;

		this.setState({ connectionState: nextState });

		const didReconnect =
			nextState === Lifecycle.Connected && prevState !== Lifecycle.Connected;

		if (didReconnect) {
			this.start();
		}
	};

	//Do not run during componentWillReceiveProps!
	start() {
		const { xterm } = this;
		const { capsule } = this.props;
		if (!capsule) return;
		if (!xterm) return;

		capsule.log().then((exec: RemoteExec) => {
			const attachAddon = new CapsuleTermAddon(exec);
			xterm.loadAddon(attachAddon);
		});
	}

	render() {
		const { capsule } = this.props;

		return (
			<div
				ref={this.anchorRef}
				className={classnames(!!capsule && styles.running, styles.terminal)}
			/>
		);
	}
}
